/**
 * สถานะเซสชันของแอป — แหล่งความจริงเดียวว่า "ตอนนี้ถือ token อยู่หรือไม่"
 *
 * เก็บเป็น external store ธรรมดาแทน React context เพราะผู้แจ้งว่าเซสชันจบมีสองทาง
 * และทางหนึ่งไม่ได้อยู่ใน React เลย:
 *
 * 1. ผู้ใช้กดออกจากระบบ (มาจาก component)
 * 2. endpoint ใดก็ตามตอบ `401` (มาจาก `authorizedFetch` ซึ่งเป็นฟังก์ชันธรรมดา)
 *
 * ทางที่สองคือเหตุผลที่ D-049 บังคับให้ล้าง token ทั้งตอนออกจากระบบ **และ** ตอนเจอ
 * `401` — session ที่ถูก revoke แล้วต้องไม่ทิ้งแอปไว้ในสถานะกึ่งเข้าสู่ระบบ ที่เมนู
 * ยังอยู่แต่ทุกคำขอถูกปฏิเสธ store ตัวนี้จึงถูกเขียนได้จากนอก React และทุกหน้าจอ
 * ที่ `useSession` จะถูกปลุกพร้อมกัน
 *
 * **token อยู่ในหน่วยความจำเป็นหลัก และ mirror ลง `localStorage`** — ไม่ใช่ตรงข้าม
 * เบราว์เซอร์ที่ปิด storage จึงยังใช้งานได้จนกว่าจะปิดแท็บ แทนที่จะวนกลับมาหน้า
 * login ทันทีหลังกดเข้าสู่ระบบสำเร็จ; `persisted = false` บอกผู้ใช้ตามจริงว่า
 * เซสชันนี้จะไม่รอดการรีเฟรช (ดู `tokenStorage.ts`)
 */

import { useSyncExternalStore } from 'react';

import { logout } from './logoutClient';
import { clearStoredToken, readStoredToken, writeStoredToken } from './tokenStorage';

/**
 * เหตุผลที่เซสชันจบ — ใช้เลือกข้อความบนหน้า Login เท่านั้น
 *
 * `UNAUTHORIZED` ไม่ใช่ "รหัสผ่านผิด" แต่แปลว่า token ที่ถืออยู่ใช้ไม่ได้แล้ว
 * (หมดอายุ, ถูก revoke, หรือผู้ดูแลระบบรีเซ็ตรหัสจนฆ่า session ตาม D-048)
 */
export type SessionEndReason = 'SIGNED_OUT' | 'UNAUTHORIZED';

export interface SessionState {
  /** token ที่ใช้ยิง API — `null` แปลว่ายังไม่ได้เข้าสู่ระบบ */
  readonly token: string | null;
  /** token ถูกเขียนลง `localStorage` สำเร็จหรือไม่ */
  readonly persisted: boolean;
  readonly endedReason: SessionEndReason | null;
}

const SIGNED_OUT: SessionState = { token: null, persisted: false, endedReason: null };

/**
 * snapshot ถูก cache ไว้เป็น object เดียว เพราะ `useSyncExternalStore` เปรียบเทียบ
 * ด้วย `Object.is` — การสร้าง object ใหม่ทุกครั้งที่อ่านจะทำให้ React มองว่า store
 * เปลี่ยนตลอดเวลาแล้ว re-render ไม่รู้จบ
 */
let snapshot: SessionState | null = null;

const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  snapshot = next;
  // copy ก่อนวน เผื่อ listener ยกเลิกตัวเองระหว่างการแจ้ง
  for (const listener of [...listeners]) {
    listener();
  }
}

/** สถานะปัจจุบัน — อ่าน `localStorage` ครั้งแรกครั้งเดียวแล้ว cache */
export function getSessionState(): SessionState {
  if (snapshot === null) {
    const token = readStoredToken();
    snapshot = token === null ? SIGNED_OUT : { token, persisted: true, endedReason: null };
  }
  return snapshot;
}

export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** บันทึก token ที่เพิ่งได้จาก `POST /v1/auth/login` */
export function startSession(token: string): void {
  const value = token.trim();
  if (value === '') {
    // token ว่างไม่ใช่ token — ถือว่ายังไม่ได้เข้าสู่ระบบ ดีกว่าเก็บค่าที่ยิงแล้วโดน 401 แน่ ๆ
    endSession(null);
    return;
  }
  publish({ token: value, persisted: writeStoredToken(value), endedReason: null });
}

/**
 * ล้าง token ทิ้ง — ล้าง storage เสมอ แม้สถานะในหน่วยความจำจะว่างอยู่แล้ว
 *
 * ไม่ publish ซ้ำถ้าสถานะไม่เปลี่ยน เพราะ `401` หลายใบจากคำขอที่ยิงขนานกันจะกลาย
 * เป็นการแจ้ง listener รัว ๆ โดยไม่มีอะไรเปลี่ยน
 */
export function endSession(reason: SessionEndReason | null = null): void {
  const current = getSessionState();
  clearStoredToken();
  if (current.token === null && current.endedReason === reason) {
    return;
  }
  publish({ token: null, persisted: false, endedReason: reason });
}

export interface SignOutOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  /** inject ได้เพื่อการทดสอบ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * ออกจากระบบตามที่ผู้ใช้สั่ง — **จบ session ฝั่งเซิร์ฟเวอร์ด้วย ไม่ใช่แค่ลืม token**
 *
 * นี่คือจุดเดียวของทั้งแอปที่เรียก `POST /v1/auth/logout` (D-057) ทั้ง Desktop header
 * และเครื่องสแกนลงมาที่นี่ตัวเดียวกัน และเครื่องสแกนคือตัวที่สำคัญที่สุด: D-046 ให้
 * handheld ถือ session 16 ชั่วโมงโดยไม่มี idle timeout เพื่อให้ operator login ครั้ง
 * เดียวต่อกะ — session ที่ไม่ถูกปิดบนเครื่องนั้นจึงมีอายุต่อไปได้อีกเกือบทั้งกะ
 *
 * **ลำดับสำคัญกว่าผลลัพธ์**:
 *
 * 1. ยิงคำขอ **ก่อน** ด้วย token ที่ยังอยู่ — ถ้าล้างก่อน คำขอจะไม่มีอะไรไปยืนยันตัวตน
 * 2. แล้ว `endSession` ทันที **โดยไม่รอคำตอบและไม่สนว่าคำตอบคืออะไร** — `204`, `401`,
 *    `500`, เครื่องออฟไลน์ หรือคำขอที่ค้างอยู่เป็นนาที ล้วนให้ผลเดียวกันบนเครื่องนี้คือ
 *    ออกจากระบบแล้ว การผูกการล้าง token ไว้กับคำตอบของเซิร์ฟเวอร์ แปลว่าเครื่องที่เน็ต
 *    หลุดจะยังค้างสถานะเข้าสู่ระบบอยู่บนเครื่องกลางที่ใครก็หยิบได้ ซึ่งแย่กว่า session
 *    ฝั่งเซิร์ฟเวอร์ที่หมดอายุเองในอีกไม่กี่ชั่วโมงมาก
 *
 * คำขอที่ค้างอยู่ไม่ถูกยกเลิก: เซิร์ฟเวอร์ควรได้รับคำสั่ง revoke แม้ผู้ใช้จะเดินจาก
 * เครื่องไปแล้ว
 *
 * คืน `Promise` ที่ resolve เมื่อคำขอจบ (ไม่เคย reject) เพื่อให้เทสต์รอผลได้ — แต่
 * **ไม่คืนสถานะของเซิร์ฟเวอร์** โดยเจตนา เพราะไม่มีหน้าจอไหนควรเอามันไปแสดงเป็น
 * ข้อผิดพลาด โดยเฉพาะ `401` ซึ่ง contract นิยามว่าคือ "ออกจากระบบอยู่แล้ว"
 */
export function signOut(options: SignOutOptions = {}): Promise<void> {
  const { token } = getSessionState();
  // ไม่มี token ก็ไม่มีอะไรให้เซิร์ฟเวอร์ revoke และไม่มีอะไรจะยืนยันตัวตน
  const pending =
    token === null
      ? Promise.resolve()
      : logout({
          token,
          ...(options.apiBaseUrl === undefined ? {} : { apiBaseUrl: options.apiBaseUrl }),
          ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
        });

  endSession('SIGNED_OUT');

  return pending.then(() => undefined);
}

/** subscribe สถานะเซสชันจาก component */
export function useSession(): SessionState {
  return useSyncExternalStore(subscribeToSession, getSessionState, getSessionState);
}
